import React from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Collapse,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import RevisionHistoryTable, {
  type RevisionDiffState,
} from "./components/RevisionHistoryTable";
import SpecsRevisionService, {
  type SpecsHistoryRevision,
  type SpecsRevisionRecord,
  type SpecType,
} from "../../service/SpecsRevisionService";
import { createStyles, palette } from "../../styles/theme";
import { formatSpecsRevisionDiff } from "./specsRevisionDiff";
import { formatPromotionSuccessMessage } from "./specsRevisionPromotion";

type GenericSpecsHistoryPageProps = {
  specType: SpecType;
  title: string;
  specName?: string;
  productVersion?: string;
  subtitle?: string;
  onBack: (specName?: string) => void;
  onLoadRevision?: (revisionId: string) => Promise<void>;
  loadingRevisionId?: string | null;
  historyState?: GenericSpecsHistoryState;
};

type RevisionPayloadState = {
  status: "idle" | "loading" | "ready" | "error";
  revision?: SpecsRevisionRecord;
  error?: string;
};

type BaseSpecsEntity = Record<string, unknown>;
type ProductSpecsEntity = BaseSpecsEntity & { versions: BaseSpecsEntity[] };
type SpecsCatalogCollectionName =
  | "families"
  | "products"
  | "fabrics"
  | "decorations";

type PublishComparisonState = {
  status: "idle" | "loading" | "ready" | "error";
  revisionId?: string;
  text?: string;
  error?: string;
};

export type GenericSpecsHistoryState = {
  revisions: SpecsHistoryRevision[];
  revisionDiffs: Record<string, RevisionDiffState>;
  loading: boolean;
  error: string | null;
  pageIndex: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  activeRevisionId?: string | null;
  loadingRevisionId?: string | null;
  publishingRevisionId?: string | null;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onLoadRevision?: (revisionId: string) => Promise<void>;
  onPublishRevision?: (revisionId: string) => Promise<void>;
  onExpandRevision?: (
    revision: SpecsHistoryRevision,
    fallbackRevisionId: string | null,
    compareAgainstBaseSpecs: boolean
  ) => Promise<void> | void;
};

const HISTORY_PAGE_SIZE = 20;
const collectionNameByType = {
  family: "families",
  product: "products",
  fabric: "fabrics",
  decoration: "decorations",
} satisfies Record<SpecType, SpecsCatalogCollectionName>;

const styles = createStyles({
  diffPre: {
    m: 0,
    p: 1.5,
    borderRadius: 1,
    bgcolor: palette.grey.lightBackground,
    border: `1px solid ${palette.grey.lightGrey}`,
    fontFamily: "monospace",
    fontSize: 12,
    lineHeight: 1.5,
    overflowX: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
});

export default function GenericSpecsHistoryPage({
  specType,
  title,
  specName,
  productVersion,
  subtitle,
  onBack,
  onLoadRevision,
  loadingRevisionId = null,
  historyState,
}: GenericSpecsHistoryPageProps) {
  const [revisions, setRevisions] = React.useState<SpecsHistoryRevision[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [pageIndex, setPageIndex] = React.useState(0);
  const [nextCursor, setNextCursor] = React.useState<string | null>(null);
  const [cursorStack, setCursorStack] = React.useState<Array<string | null>>([
    null,
  ]);
  const [revisionPayloads, setRevisionPayloads] = React.useState<
    Record<string, RevisionPayloadState>
  >({});
  const [revisionDiffs, setRevisionDiffs] = React.useState<
    Record<string, RevisionDiffState>
  >({});
  const [publishingRevisionId, setPublishingRevisionId] = React.useState<
    string | null
  >(null);
  const [revisionPendingPublish, setRevisionPendingPublish] =
    React.useState<SpecsHistoryRevision | null>(null);
  const [publishComparisonExpanded, setPublishComparisonExpanded] =
    React.useState(false);
  const [publishComparison, setPublishComparison] =
    React.useState<PublishComparisonState>({ status: "idle" });
  const [baseSpecsEntityCache, setBaseSpecsEntityCache] = React.useState<
    Record<string, BaseSpecsEntity>
  >({});
  const [nextEnvBaseSpecsEntityCache, setNextEnvBaseSpecsEntityCache] =
    React.useState<Record<string, BaseSpecsEntity>>({});

  const closePublishDialog = React.useCallback(() => {
    setRevisionPendingPublish(null);
    setPublishComparisonExpanded(false);
    setPublishComparison({ status: "idle" });
  }, []);

  const currentCursor = cursorStack[pageIndex] ?? null;
  const isControlled = !!historyState;

  const getBaseSpecsEntity = React.useCallback(
    async (requestedSpecType: SpecType, requestedSpecName: string) => {
      const cacheKey = `${requestedSpecType}:${requestedSpecName}`;
      const cachedEntity = baseSpecsEntityCache[cacheKey];
      if (cachedEntity) {
        return cachedEntity;
      }

      const response = await SpecsRevisionService.getPublishedSpecsCatalog();
      const collection = response.data[
        collectionNameByType[requestedSpecType]
      ] as BaseSpecsEntity[];
      const entity = collection.find(
        (candidate) =>
          candidate.name === requestedSpecName ||
          (requestedSpecType === "decoration" &&
            candidate.id === requestedSpecName)
      );

      if (!entity) {
        throw new Error(
          `Failed to load base specs for ${requestedSpecType} ${requestedSpecName}`
        );
      }

      setBaseSpecsEntityCache((current) => ({
        ...current,
        [cacheKey]: entity,
      }));

      return entity;
    },
    [baseSpecsEntityCache]
  );

  const getNextEnvBaseSpecsEntity = React.useCallback(
    async (requestedSpecType: SpecType, requestedSpecName: string) => {
      const cacheKey = `${requestedSpecType}:${requestedSpecName}`;
      const cachedEntity = nextEnvBaseSpecsEntityCache[cacheKey];
      if (cachedEntity) {
        return cachedEntity;
      }

      const response =
        await SpecsRevisionService.getNextEnvPublishedSpecsCatalog();
      const collection = response.data.specs[
        collectionNameByType[requestedSpecType]
      ] as BaseSpecsEntity[];
      const entity = collection.find(
        (candidate) =>
          candidate.name === requestedSpecName ||
          (requestedSpecType === "decoration" &&
            candidate.id === requestedSpecName)
      );

      if (!entity) {
        throw new Error(
          `Failed to load next environment specs for ${requestedSpecType} ${requestedSpecName}`
        );
      }

      setNextEnvBaseSpecsEntityCache((current) => ({
        ...current,
        [cacheKey]: entity,
      }));

      return entity;
    },
    [nextEnvBaseSpecsEntityCache]
  );

  const fetchRevision = React.useCallback(
    async (revisionId: string) => {
      const cachedRevision = revisionPayloads[revisionId];
      if (cachedRevision?.status === "ready" && cachedRevision.revision) {
        return cachedRevision.revision;
      }

      setRevisionPayloads((current) => ({
        ...current,
        [revisionId]: {
          status: "loading",
        },
      }));

      if (!specName) {
        throw new Error("Select a spec before loading a revision");
      }

      const response = await SpecsRevisionService.getSpecRevision(
        specType,
        specName,
        revisionId
      );
      setRevisionPayloads((current) => ({
        ...current,
        [revisionId]: {
          status: "ready",
          revision: response.data,
        },
      }));
      return response.data;
    },
    [revisionPayloads, specName, specType]
  );

  const loadPage = React.useCallback(async () => {
    if (isControlled) {
      return;
    }

    if (!specName) {
      setRevisions([]);
      setNextCursor(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await SpecsRevisionService.listSpecRevisions(
        specType,
        specName,
        {
          limit: HISTORY_PAGE_SIZE,
          cursor: currentCursor,
        }
      );
      setRevisions(response.data.revisions ?? []);
      setNextCursor(response.data.nextCursor ?? null);
    } catch (nextError) {
      setError(
        nextError instanceof Error
          ? nextError.message
          : "Failed to load revision history"
      );
    } finally {
      setLoading(false);
    }
  }, [currentCursor, isControlled, specName, specType]);

  React.useEffect(() => {
    if (isControlled) {
      return;
    }

    setCursorStack([null]);
    setPageIndex(0);
    setNextCursor(null);
    setRevisions([]);
    setError(null);
    setRevisionPayloads({});
    setRevisionDiffs({});
    setPublishingRevisionId(null);
  }, [isControlled, specName, specType]);

  React.useEffect(() => {
    if (isControlled) {
      return;
    }

    void loadPage();
  }, [isControlled, loadPage]);

  const handleExpandRevision = React.useCallback(
    async (
      revision: SpecsHistoryRevision,
      fallbackRevisionId: string | null,
      compareAgainstBaseSpecs: boolean
    ) => {
      if (historyState?.onExpandRevision) {
        await historyState.onExpandRevision(
          revision,
          fallbackRevisionId,
          compareAgainstBaseSpecs
        );
        return;
      }

      if (!specName) {
        return;
      }

      const existingDiff = revisionDiffs[revision.revisionId];
      if (
        existingDiff?.status === "loading" ||
        existingDiff?.status === "ready"
      ) {
        return;
      }
      const compareRevisionId = revision.sourceRevisionId ?? fallbackRevisionId;

      setRevisionDiffs((current) => ({
        ...current,
        [revision.revisionId]: {
          status: "loading",
        },
      }));

      try {
        const currentRevision = await fetchRevision(revision.revisionId);
        let previousEntity: BaseSpecsEntity;
        if (compareAgainstBaseSpecs) {
          previousEntity = await getBaseSpecsEntity(specType, specName);
        } else {
          if (!compareRevisionId) {
            throw new Error("Missing source revision to compare");
          }
          previousEntity = (await fetchRevision(compareRevisionId)).json;
        }

        let previousSnapshot = previousEntity;
        let currentSnapshot: BaseSpecsEntity = currentRevision.json;
        if (specType === "product" && productVersion) {
          const previousVersion = (
            previousEntity as ProductSpecsEntity
          ).versions.find((candidate) => candidate.name === productVersion);
          if (!previousVersion) {
            const sourceName = compareAgainstBaseSpecs
              ? "Base specs"
              : "Source revision";
            throw new Error(
              `${sourceName} product does not include version ${productVersion}`
            );
          }

          const currentVersion = (
            currentRevision.json as ProductSpecsEntity
          ).versions.find((candidate) => candidate.name === productVersion);
          if (!currentVersion) {
            throw new Error(
              `Current revision product does not include version ${productVersion}`
            );
          }

          previousSnapshot = previousVersion;
          currentSnapshot = currentVersion;
        }

        setRevisionDiffs((current) => ({
          ...current,
          [revision.revisionId]: {
            status: "ready",
            text: formatSpecsRevisionDiff(previousSnapshot, currentSnapshot),
          },
        }));
      } catch (nextError) {
        setRevisionDiffs((current) => ({
          ...current,
          [revision.revisionId]: {
            status: "error",
            error:
              nextError instanceof Error
                ? nextError.message
                : "Failed to load diff",
          },
        }));
      }
    },
    [
      getBaseSpecsEntity,
      fetchRevision,
      historyState,
      productVersion,
      revisionDiffs,
      specName,
      specType,
    ]
  );

  const loadPublishComparison = React.useCallback(
    async (revision: SpecsHistoryRevision) => {
      if (!specName) {
        return;
      }

      if (
        publishComparison.revisionId === revision.revisionId &&
        (publishComparison.status === "loading" ||
          publishComparison.status === "ready")
      ) {
        return;
      }

      setPublishComparison({
        status: "loading",
        revisionId: revision.revisionId,
      });

      try {
        const [revisionRecord, publishedEntity] = await Promise.all([
          fetchRevision(revision.revisionId),
          getNextEnvBaseSpecsEntity(specType, specName),
        ]);

        let publishedSnapshot = publishedEntity;
        let revisionSnapshot: BaseSpecsEntity = revisionRecord.json;
        if (specType === "product" && productVersion) {
          const publishedVersion = (
            publishedEntity as ProductSpecsEntity
          ).versions.find((candidate) => candidate.name === productVersion);
          if (!publishedVersion) {
            throw new Error(
              `Published product does not include version ${productVersion}`
            );
          }

          const revisionVersion = (
            revisionRecord.json as ProductSpecsEntity
          ).versions.find((candidate) => candidate.name === productVersion);
          if (!revisionVersion) {
            throw new Error(
              `Revision product does not include version ${productVersion}`
            );
          }

          publishedSnapshot = publishedVersion;
          revisionSnapshot = revisionVersion;
        }

        setPublishComparison({
          status: "ready",
          revisionId: revision.revisionId,
          text: formatSpecsRevisionDiff(publishedSnapshot, revisionSnapshot),
        });
      } catch (nextError) {
        setPublishComparison({
          status: "error",
          revisionId: revision.revisionId,
          error:
            nextError instanceof Error
              ? nextError.message
              : "Failed to load prod comparison",
        });
      }
    },
    [
      fetchRevision,
      getNextEnvBaseSpecsEntity,
      productVersion,
      publishComparison.revisionId,
      publishComparison.status,
      specName,
      specType,
    ]
  );

  const handlePublishRevision = historyState?.onPublishRevision;
  const publishRevision = React.useCallback(
    async (revision: SpecsHistoryRevision) => {
      if (handlePublishRevision) {
        await handlePublishRevision(revision.revisionId);
        return;
      }

      if (!specName) {
        return;
      }

      setPublishingRevisionId(revision.revisionId);
      try {
        const promoteResponse =
          await SpecsRevisionService.promoteRevisionToNext(
            specType,
            specName,
            revision.revisionId
          );
        toast.success(formatPromotionSuccessMessage(promoteResponse.data));
      } catch (nextError) {
        toast.error(
          nextError instanceof Error
            ? nextError.message
            : "Failed to promote revision to next env"
        );
      } finally {
        setPublishingRevisionId(null);
      }
    },
    [handlePublishRevision, specName, specType]
  );

  if (!specName) {
    return null;
  }

  const resolvedRevisions = historyState?.revisions ?? revisions;
  const resolvedRevisionDiffs = historyState?.revisionDiffs ?? revisionDiffs;
  const resolvedLoading = historyState?.loading ?? loading;
  const resolvedError = historyState?.error ?? error;
  const resolvedPageIndex = historyState?.pageIndex ?? pageIndex;
  const resolvedHasPreviousPage =
    historyState?.hasPreviousPage ?? resolvedPageIndex > 0;
  const resolvedHasNextPage = historyState?.hasNextPage ?? !!nextCursor;
  const resolvedActiveRevisionId = historyState?.activeRevisionId ?? null;
  const resolvedLoadingRevisionId =
    historyState?.loadingRevisionId ?? loadingRevisionId;
  const handleOpenRevision = historyState?.onLoadRevision ?? onLoadRevision;
  const handlePreviousPage =
    historyState?.onPreviousPage ??
    (() => setPageIndex((current) => Math.max(current - 1, 0)));
  const handleNextPage =
    historyState?.onNextPage ??
    (() => {
      if (!nextCursor) {
        return;
      }

      setCursorStack((current) => {
        const next = current.slice(0, pageIndex + 1);
        next.push(nextCursor);
        return next;
      });
      setPageIndex((current) => current + 1);
    });
  const resolvedPublishingRevisionId =
    historyState?.publishingRevisionId ?? publishingRevisionId;
  const openPublishDialog = (revision: SpecsHistoryRevision) => {
    setRevisionPendingPublish(revision);
    setPublishComparisonExpanded(true);
    setPublishComparison({ status: "idle" });
    void loadPublishComparison(revision);
  };

  return (
    <>
      <RevisionHistoryTable
        title={`${title} history`}
        subtitle={subtitle ?? specName}
        emptyMessage={`No saved revisions yet for this ${specType}.`}
        revisions={resolvedRevisions}
        revisionDiffs={resolvedRevisionDiffs}
        loading={resolvedLoading}
        error={resolvedError}
        pageIndex={resolvedPageIndex}
        hasPreviousPage={resolvedHasPreviousPage}
        hasNextPage={resolvedHasNextPage}
        loadingRevisionId={resolvedLoadingRevisionId}
        activeRevisionId={resolvedActiveRevisionId}
        publishingRevisionId={resolvedPublishingRevisionId}
        specName={specName}
        onOpenRevision={handleOpenRevision}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        onBack={onBack}
        onExpandRevision={handleExpandRevision}
        onPublishRevision={openPublishDialog}
      />
      <Dialog
        open={!!revisionPendingPublish}
        onClose={closePublishDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Publier en prod?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Assurez-vous qu&apos;un développeur a approuvé ce changement avant
            de publier la révision{" "}
            {revisionPendingPublish?.revisionId.slice(0, 8)} en production.
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            <Box
              component="button"
              type="button"
              onClick={() => {
                const nextExpanded = !publishComparisonExpanded;
                setPublishComparisonExpanded(nextExpanded);
                if (nextExpanded && revisionPendingPublish) {
                  void loadPublishComparison(revisionPendingPublish);
                }
              }}
              sx={{
                alignItems: "center",
                background: "transparent",
                border: 0,
                borderBottom: `1px solid ${palette.grey.lightGrey}`,
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                p: 0,
                pb: 1,
                textAlign: "left",
                width: "100%",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Comparer avec la version prod actuelle
              </Typography>
              <ExpandMoreIcon
                fontSize="small"
                sx={{
                  transform: publishComparisonExpanded
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              />
            </Box>
            <Collapse in={publishComparisonExpanded} timeout="auto">
              <Box sx={{ mt: 1.5 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Diff prod actuelle → révision à publier
                </Typography>
                <Box component="pre" sx={styles.diffPre}>
                  {publishComparison.revisionId !==
                  revisionPendingPublish?.revisionId
                    ? "Chargement de la comparaison..."
                    : publishComparison.status === "loading"
                      ? "Chargement de la comparaison..."
                      : publishComparison.status === "error"
                        ? publishComparison.error ||
                          "Impossible de charger la comparaison"
                        : publishComparison.status === "ready"
                          ? publishComparison.text
                          : "Ouvrir pour charger la comparaison"}
                </Box>
              </Box>
            </Collapse>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePublishDialog} size="small">
            Annuler
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              const revision = revisionPendingPublish;
              closePublishDialog();
              if (revision) {
                void publishRevision(revision);
              }
            }}
          >
            Publier en prod
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
